import React, { JSX } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material';
import {
  AutoFixHigh,
  Business,
  CheckCircle,
  CloudUpload,
  Description,
  Work,
} from '@mui/icons-material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import * as Types from 'types/types';
import { JobCreatorProcessing } from './JobCreatorProcessing';
import { JobCreatorRequirements } from './JobCreatorRequirements';
import { JobProcessingPhase } from './jobCreatorUtils';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadBox = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.primary.main}`,
  borderRadius:
    (typeof theme.shape.borderRadius === 'string'
      ? parseInt(theme.shape.borderRadius)
      : theme.shape.borderRadius) * 2,
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: theme.palette.action.hover,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
    borderColor: theme.palette.primary.dark,
  },
}));

export function JobCreatorForm(props: {
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  isMobile: boolean;
  isProcessing: boolean;
  jobDescription: string;
  jobTitle: string;
  company: string;
  summary: string;
  jobRequirements: Types.JobRequirements | null;
  jobStatus: string;
  jobStatusType: Types.ApiActivityType | null;
  thinkingMarkdown: string;
  generatedMarkdown: string;
  processingPhase: JobProcessingPhase;
  onUploadClick: () => void;
  onJobUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onExtractRequirements: () => void;
  onJobDescriptionChange: (value: string) => void;
  onJobTitleChange: (value: string) => void;
  onCompanyChange: (value: string) => void;
}): JSX.Element {
  return (
    <Box sx={{ width: '100%', p: 1 }}>
      <Card elevation={3} sx={{ mb: 4 }}>
        <CardHeader
          title="Job Information"
          subheader="Upload a job description or enter details manually"
          avatar={<Work color="primary" />}
        />
        <CardContent>
          <Grid container spacing={3}>
            <UploadColumn {...props} />
            <ManualEntryColumn {...props} />
          </Grid>
          <JobCreatorProcessing {...props} />
        </CardContent>
      </Card>
      <JobDetailsCard {...props} />
      {props.summary !== '' && (
        <Card elevation={2} sx={{ mt: 3 }}>
          <CardHeader title="Job Summary" avatar={<CheckCircle color="success" />} sx={{ pb: 1 }} />
          <CardContent sx={{ pt: 0 }}>{props.summary}</CardContent>
        </Card>
      )}
      <JobCreatorRequirements jobRequirements={props.jobRequirements} />
    </Box>
  );
}

function UploadColumn(props: Parameters<typeof JobCreatorForm>[0]): JSX.Element {
  return (
    <Grid size={{ xs: 12, md: 6 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <CloudUpload sx={{ mr: 1 }} /> Upload Job Description
      </Typography>
      <UploadBox onClick={props.onUploadClick}>
        <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Drop your job description here
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Supported formats: PDF, DOCX, TXT, MD
        </Typography>
        <Button variant="contained" startIcon={<FileUploadIcon />} disabled={props.isProcessing}>
          Choose File
        </Button>
      </UploadBox>
      <VisuallyHiddenInput
        ref={props.fileInputRef}
        type="file"
        accept=".txt,.md,.docx,.pdf"
        onChange={props.onJobUpload}
      />
    </Grid>
  );
}

function ManualEntryColumn(props: Parameters<typeof JobCreatorForm>[0]): JSX.Element {
  return (
    <Grid size={{ xs: 12, md: 6 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <Description sx={{ mr: 1 }} /> Or Enter Manually
      </Typography>
      <TextField
        fullWidth
        multiline
        rows={props.isMobile ? 8 : 12}
        placeholder="Paste or type the job description here..."
        variant="outlined"
        value={props.jobDescription}
        onChange={e => props.onJobDescriptionChange(e.target.value)}
        disabled={props.isProcessing}
        sx={{ mb: 2 }}
      />
      {props.jobRequirements === null && props.jobDescription && (
        <Button
          variant="outlined"
          onClick={props.onExtractRequirements}
          startIcon={<AutoFixHigh />}
          disabled={props.isProcessing}
          fullWidth={props.isMobile}
        >
          Extract Requirements
        </Button>
      )}
    </Grid>
  );
}

function JobDetailsCard(props: Parameters<typeof JobCreatorForm>[0]): JSX.Element {
  return (
    <Card elevation={3} sx={{ mb: 4 }}>
      <CardHeader
        title="Job Details"
        subheader="Enter specific information about the position"
        avatar={<Business color="primary" />}
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Job Title"
              variant="outlined"
              value={props.jobTitle}
              onChange={e => props.onJobTitleChange(e.target.value)}
              required
              disabled={props.isProcessing}
              InputProps={{ startAdornment: <Work sx={{ mr: 1, color: 'text.secondary' }} /> }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Company"
              variant="outlined"
              value={props.company}
              onChange={e => props.onCompanyChange(e.target.value)}
              required
              disabled={props.isProcessing}
              InputProps={{ startAdornment: <Business sx={{ mr: 1, color: 'text.secondary' }} /> }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
